import { moveToFulfilled, moveToPending, moveToRejected, remove } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import { CreateTeamsMeetingFulfilledAction } from '../../domains/createTeamsMeeting/actions.types';
import { DeleteTeamsMeetingFulfilledAction } from '../../domains/deleteTeamsMeeting/actions.types';
import { EditTeamsMeetingFulfilledAction } from '../../domains/editTeamsMeeting/actions.types';
import { MarkAllNotificationsAsSeenFulfilledAction } from '../../domains/notificationFeeds/actions.types';
import { NotificationLoadMoreFulfilledAction } from '../../domains/notificationPaginations/actions.types';
import { ScheduleAndCreateTeamsMeetingFulfilledAction } from '../../domains/scheduleAndCreateTeamsMeeting/actions.types';
import { TeamsMeetingLoadAction } from '../../domains/teamsMeetingLoad/actions.types';
import {
  TeamsMeetingAdminSettingsUpdateAction,
  TeamsMeetingRealtimeUpdates,
} from '../../domains/teamsMeetingRealtime/actions.types';
import { TeamsMeetingsByOrganizerFulfilledAction } from '../../domains/teamsMeetingsByOrganizer/actions.types';

import { TeamsMeeting, TeamsMeetings } from './types';

export type TeamsMeetingAction =
  | CreateTeamsMeetingFulfilledAction
  | DeleteTeamsMeetingFulfilledAction
  | EditTeamsMeetingFulfilledAction
  | MarkAllNotificationsAsSeenFulfilledAction
  | NotificationLoadMoreFulfilledAction
  | ScheduleAndCreateTeamsMeetingFulfilledAction
  | TeamsMeetingAdminSettingsUpdateAction
  | TeamsMeetingLoadAction
  | TeamsMeetingsByOrganizerFulfilledAction;

const initialState: TeamsMeetings = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const updateTeamsMeetingRealtimeData = (state: TeamsMeetings, updates: TeamsMeetingRealtimeUpdates) => {
  const {
    officeMeetingId,
    isAnonymousPostingEnabled,
    isPrivateReplyEnabled,
    isReplyToConversationEnabled,
    isThreadStarterAdminModerated,
    isThreadStarterAdminOnly,
    isThreadUpvoteEnabled,
    status,
  } = updates;
  const teamsMeetings = state.fulfilled.byId;
  const teamsMeeting = Object.values(teamsMeetings).find(
    (teamsMeeting) => teamsMeeting.officeMeetingId === officeMeetingId
  );

  if (!teamsMeeting) {
    return state;
  }

  const entry = {
    ...teamsMeeting,
    isAnonymousPostingEnabled,
    isPrivateReplyEnabled,
    isReplyToConversationEnabled,
    isThreadStarterAdminModerated,
    isThreadStarterAdminOnly,
    isThreadUpvoteEnabled,
    status,
    viewerCanReply: isReplyToConversationEnabled || teamsMeeting.viewerIsAdmin,
    viewerCanStartThread: !isThreadStarterAdminOnly || teamsMeeting.viewerIsAdmin,
  };

  return {
    ...state,
    fulfilled: {
      ...state.fulfilled,
      byId: {
        ...state.fulfilled.byId,
        [teamsMeeting.id]: entry,
      },
    },
  };
};

const updateSilentReloadPending = (state: TeamsMeetings, teamsMeetingId: TeamsMeeting['id']) => {
  const teamsMeetings = state.fulfilled.byId;
  const teamsMeeting = Object.values(teamsMeetings).find((teamsMeeting) => teamsMeeting.id === teamsMeetingId);

  if (!teamsMeeting) {
    return state;
  }

  const entry = {
    ...teamsMeeting,
    isSilentlyReloading: true,
  };

  return {
    ...state,
    fulfilled: {
      ...state.fulfilled,
      byId: {
        ...state.fulfilled.byId,
        [teamsMeeting.id]: entry,
      },
    },
  };
};

const reducer: Reducer<TeamsMeetings, TeamsMeetingAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'CREATE_TEAMS_MEETING.FULFILLED':
    case 'MARK_ALL_NOTIFICATIONS_AS_SEEN.LOAD_FULFILLED':
    case 'NOTIFICATION.LOAD_MORE_FULFILLED':
    case 'SCHEDULE_AND_CREATE_TEAMS_MEETING.FULFILLED':
    case 'TEAMS_MEETINGS_BY_ORGANIZER.LOAD_FULFILLED':
    case 'EDIT_TEAMS_MEETING.FULFILLED':
      return moveToFulfilled(state, action.payload.entities.teamsMeetings);
    case 'DELETE_TEAMS_MEETING.FULFILLED':
      return remove(state, [action.payload.id]);
    case 'TEAMS_MEETING.LOAD_PENDING':
      return moveToPending(state, [action.payload]);
    case 'TEAMS_MEETING.SILENT_RELOAD_PENDING':
      return updateSilentReloadPending(state, action.payload);
    case 'TEAMS_MEETING.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.teamsMeetings);
    case 'TEAMS_MEETING.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.teamsMeetingId], action.payload.error);
    case 'TEAMS_MEETING_ADMIN_SETTINGS.UPDATE':
      return updateTeamsMeetingRealtimeData(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
